import { useState } from 'react';
import { api } from '../api/client';
import { USER_ROLE_LABEL } from '../constants/labels';
import { getErrorMessage } from '../types/api-error';
import type { ManagedUser } from '../types/admin';

interface TransferOwnerModalProps {
  fromUser: ManagedUser;
  onClose: () => void;
  onSuccess: () => void;
}

const MOBILE_REGEX = /^\d{11}$/;

export function TransferOwnerModal({
  fromUser,
  onClose,
  onSuccess,
}: TransferOwnerModalProps) {
  const [mobile, setMobile] = useState('');
  const [targetUser, setTargetUser] = useState<ManagedUser | null>(null);
  const [searching, setSearching] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferLimit, setTransferLimit] = useState(true);
  const [error, setError] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setTargetUser(null);
    const trimmed = mobile.trim();
    if (!trimmed) {
      setError('请输入目标用户手机号');
      return;
    }
    if (!MOBILE_REGEX.test(trimmed)) {
      setError('请输入 11 位手机号');
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get<ManagedUser[]>('/users', {
        params: { mobile: trimmed },
      });
      const exact = data.find((u) => u.mobile === trimmed);
      if (!exact) {
        setError('未找到该手机号的用户');
        return;
      }
      if (exact.id === fromUser.id) {
        setError('不能转移给自己');
        return;
      }
      if (exact.status !== 'active') {
        setError('目标用户状态不是正常，无法接收');
        return;
      }
      setTargetUser(exact);
    } catch (err) {
      setError(getErrorMessage(err, '搜索失败'));
    } finally {
      setSearching(false);
    }
  };

  const handleTransfer = async () => {
    if (!targetUser) return;
    setError('');
    setTransferring(true);
    try {
      await api.post('/users/transfer-owner', {
        fromUserId: fromUser.id,
        toUserId: targetUser.id,
        transferStoreCreationLimit: transferLimit,
      });
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err, '转移失败'));
      setTransferring(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => {
        if (!transferring && e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-5 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          转移 owner 角色
        </h3>

        <div className="text-sm text-gray-600 mb-4">
          当前用户：
          <span className="font-medium text-gray-900">
            {fromUser.mobile}（{fromUser.nickname || '-'}）
          </span>
        </div>

        <form onSubmit={handleSearch} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              目标用户手机号
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="输入接收方的手机号"
                className="flex-1 min-w-0 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-base"
              />
              <button
                type="submit"
                disabled={searching}
                className="px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-60 whitespace-nowrap"
              >
                {searching ? '搜索中…' : '搜索'}
              </button>
            </div>
          </div>
        </form>

        {targetUser && (
          <div className="mt-3 p-3 rounded-lg bg-gray-50 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">目标用户</span>
              <span className="font-medium text-gray-900">
                {targetUser.nickname || '-'} / {targetUser.mobile}
              </span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-gray-600">当前角色</span>
              <span className="font-medium text-gray-900">
                {USER_ROLE_LABEL[targetUser.role]}
              </span>
            </div>
          </div>
        )}

        <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={transferLimit}
            onChange={(e) => setTransferLimit(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          同时转移门店创建额度
        </label>

        {error && (
          <div className="mt-4 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={transferring}
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-60"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleTransfer}
            disabled={!targetUser || transferring}
            className="flex-1 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-60"
          >
            {transferring ? '转移中…' : '确认转移'}
          </button>
        </div>
      </div>
    </div>
  );
}
